import { useCallback, useRef, useEffect, useState, useMemo } from 'react';
import type { QuoteItemDTO } from '@setvi/shared/services/react-query';
import { updateQuoteV2ItemNoteMutation } from 'Services/Mutations/QuotesV2';
import type { QuoteV2ItemWithTotalsResponse } from 'Services/Query/QuotesV2/Types';
import {
  updateItemInTree,
  updateItemFromServerResponse
} from '../utils/quote-helpers';
import { useQuoteCache } from './use-quote-cache';

interface UpdateNoteVariables {
  itemId: string;
  note: string;
}

/**
 * Debounced item note update via the dedicated v2 endpoint.
 * Applies optimistic update immediately, debounces the API call.
 */
export const useUpdateItemNote = (quoteId: string, delay = 1500) => {
  const cache = useQuoteCache(quoteId);
  const { mutationFn } = updateQuoteV2ItemNoteMutation();

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const previousItemsRef = useRef<QuoteItemDTO[] | undefined>(undefined);
  const pendingRef = useRef<UpdateNoteVariables | null>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const cancel = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    },
    []
  );

  const mutate = useCallback(
    ({ itemId, note }: UpdateNoteVariables) => {
      // Capture pre-change state on first edit only
      if (!timeoutRef.current) {
        previousItemsRef.current = cache.getItems();
      }

      pendingRef.current = { itemId, note };

      // Optimistic update immediately
      cache.setItems(old => updateItemInTree(old || [], itemId, { note }));

      cancel();

      timeoutRef.current = setTimeout(async () => {
        timeoutRef.current = null;
        setIsLoading(true);
        setError(null);

        const pending = pendingRef.current;
        pendingRef.current = null;

        if (!pending) {
          setIsLoading(false);
          return;
        }

        try {
          await cache.cancel();

          const data = await mutationFn({
            quoteId,
            itemId: pending.itemId,
            note: pending.note
          });

          const response = data as QuoteV2ItemWithTotalsResponse;

          if (response?.item) {
            cache.setItems(old =>
              updateItemFromServerResponse(old || [], response.item, p => ({
                note: p.note
              }))
            );
          }

          cache.invalidate();
        } catch (err) {
          if (previousItemsRef.current) {
            cache.setItems(() => previousItemsRef.current);
          }
          setError(
            err instanceof Error ? err : new Error('Note update failed')
          );
        } finally {
          setIsLoading(false);
          previousItemsRef.current = undefined;
        }
      }, delay);
    },
    [cache, cancel, delay, quoteId, mutationFn]
  );

  return useMemo(
    () => ({ mutate, cancel, isLoading, error }),
    [mutate, cancel, isLoading, error]
  );
};
